import FlexibleTable from './FlexibleTable';
import { amplitudeSeparations, SEPARATION_HELP } from '../utils/amplitudeSeparation';
import { useChannelLabel } from '../context/ChannelNumberingContext';
import React, { useMemo, useState } from 'react';
import Plot from './ThemedPlot';
import { isSossaDataset, sossaCoverage, sossaProfiles, profileSeries } from '../utils/sossa';
import { getClusterColor } from '../utils/colors';
import './SossaWidgets.css';
import SpikeWaveformGrid from './SpikeWaveformGrid';

const format = (value) => value === null ? 'Unavailable' : value.toFixed(4);
const selected = (dataset, ids) => (dataset?.clusters || []).filter((cluster) => !ids.length || ids.map(String).includes(String(cluster.id)));
const gate = (dataset, onOpenCurator, onLoadExample) => !isSossaDataset(dataset)
  ? <div role="status">
      <p>No SOSSA curator data is loaded. These widgets need a SOSSA file containing match scores, amplitudes, and waveforms.</p>
      <p>Upload a curator file with algorithmName set to SOSSA to use this widget.</p>
      {onOpenCurator && <button onClick={onOpenCurator}>Open Curator</button>}
      {onLoadExample && <button onClick={onLoadExample}>Load SOSSA example</button>}
    </div> : null;
const sourceNotice = (dataset, ids, onLoadExample) => {
  if (!isSossaDataset(dataset)) return null;
  const clusters = dataset.clusters || [];
  const channels = new Set(clusters.map((cluster) => cluster.primaryChannel).filter((channel) => channel != null));
  const spikes = clusters.reduce((sum, cluster) => sum + (cluster.spikeTimes?.length || 0), 0);
  return <div className="sossa-summary" role="status">
    <strong>{dataset.metadata?.example ? 'Example data · synthetic' : 'SOSSA'}</strong>
    <span>{clusters.length} clusters · {channels.size} channels · {spikes.toLocaleString()} spikes</span>
    {dataset.metadata?.example && onLoadExample && <button onClick={onLoadExample}>Reload SOSSA example</button>}
    <span>{ids.length ? `${ids.length} selected clusters` : `Showing all ${clusters.length} uploaded clusters`}</span>
  </div>;
};

const useChannelClusters = (dataset, ids) => {
  const [channel, setChannel] = useState('all');
  const candidates = selected(dataset, ids);
  const channels = [...new Set(candidates.map((cluster) => cluster.primaryChannel).filter((value) => value != null).map(String))].sort((a, b) => Number(a) - Number(b));
  const active = channels.includes(channel) ? channel : 'all';
  return { channels, channel: active, setChannel,
    clusters: candidates.filter((cluster) => active === 'all' || String(cluster.primaryChannel) === active) };
};
const ChannelPicker = ({ state, label }) => {
  const labelChannel = useChannelLabel();
  return <label>Channel <select aria-label={label} value={state.channel} onChange={(event) => state.setChannel(event.target.value)}>
  <option value="all">All channels</option>
  {state.channels.map((channel) => <option key={channel} value={channel}>CH {labelChannel(channel)}</option>)}
</select></label>;
};
const color = (id) => getClusterColor(Number.isFinite(Number(id)) ? Number(id) : String(id).split('').reduce((hash, char) => hash + char.charCodeAt(0), 0));
const layout = (x, y, showlegend = true) => ({
  autosize: true, margin: { l: 55, r: 16, t: 20, b: showlegend ? 110 : 50 },
  paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0.18)',
  font: { family: 'Inter, system-ui, sans-serif', color: '#cbd5e1', size: 11 },
  xaxis: { title: { text: x, standoff: 12 }, automargin: true, gridcolor: 'rgba(64,224,208,0.12)', zerolinecolor: 'rgba(64,224,208,0.25)' },
  yaxis: { title: { text: y, standoff: 8 }, automargin: true, gridcolor: 'rgba(64,224,208,0.12)', zerolinecolor: 'rgba(64,224,208,0.25)' },
  showlegend, legend: { orientation: 'h', x: 0, y: -0.25, font: { size: 10 }, bgcolor: 'rgba(0,0,0,0)' },
  hoverlabel: { bgcolor: '#11182e', bordercolor: '#40e0d0', font: { color: '#e2e8f0', size: 11 } },
  modebar: { bgcolor: 'rgba(0,0,0,0)', color: '#94a3b8', activecolor: '#40e0d0' },
  hovermode: 'closest',
});

export function SossaWaveforms({ dataset, selectedClusters = [], onOpenCurator, onLoadExample, onSpikeSelect }) {
  return <section className="sossa-widget" data-sossa="waveforms">
    {gate(dataset, onOpenCurator, onLoadExample) || <>
      {sourceNotice(dataset, selectedClusters, onLoadExample)}
      <SpikeWaveformGrid dataset={dataset} selectedClusters={selectedClusters} showConfidence onSpikeSelect={onSpikeSelect} />
    </>}
  </section>;
}

export function SossaAmplitudeProfile({ dataset, selectedClusters = [], onOpenCurator, onLoadExample }) {
  const labelChannel = useChannelLabel();
  const [window, setWindow] = useState(1);
  const [binWidth, setBinWidth] = useState(1);
  const channelState = useChannelClusters(dataset, selectedClusters);
  const profiles = useMemo(() => sossaProfiles(selected(dataset, selectedClusters).filter((cluster) => channelState.channel === 'all' || String(cluster.primaryChannel) === channelState.channel), binWidth), [dataset, selectedClusters, binWidth, channelState.channel]);
  const series = profiles.map((profile) => ({ ...profile, ...profileSeries(profile, binWidth, window) }));
  return <section className="sossa-widget" data-sossa="amplitude">
    {gate(dataset, onOpenCurator, onLoadExample) || <>
      {sourceNotice(dataset, selectedClusters, onLoadExample)}
      <div className="sossa-controls">
        <ChannelPicker state={channelState} label="SOSSA amplitude channel" />
        <label>Moving-average window (bins) <input aria-label="Moving-average window (bins)" type="number" min="1" max="1001" step="1" value={window}
          onChange={(event) => setWindow(Math.max(1, Math.min(1001, Math.trunc(Number(event.target.value)) || 1)))} /></label>
        <label>Amplitude bin width <input aria-label="Amplitude bin width" type="number" min="0.000001" step="any" value={binWidth}
          onChange={(event) => { const value = Number(event.target.value); if (Number.isFinite(value) && value > 0) setBinWidth(value); }} /></label>
      </div>
      <p>Primary-channel amplitude counts · centered moving average · zero padding at edges. Window 1 shows unsmoothed counts.</p>
      {series.some((profile) => profile.tooWide) && <p role="alert">Amplitude range exceeds 20,001 bins. Increase the bin width.</p>}
      {series.some((profile) => !profile.complete) && <p role="status">Some clusters have missing amplitudes; only supplied values are plotted.</p>}
      {series.some((profile) => profile.x.length) ? <Plot data={series.filter((profile) => profile.x.length).map((profile) => ({
        type: 'scatter', mode: profile.x.length === 1 ? 'markers' : 'lines', x: profile.x, y: profile.y,
        line: { color: color(profile.cluster.id), width: 2 }, marker: { color: color(profile.cluster.id), size: 5 },
        name: `Cluster ${profile.cluster.id} · CH ${labelChannel(profile.cluster.primaryChannel) ?? '?'}`,
      }))} layout={layout('Primary-channel amplitude (bin lower edge)', 'Mean spike count')} useResizeHandler style={{ width: '100%', height: 380 }} config={{ responsive: true, displaylogo: false }} />
        : <p>Select clusters with uploaded spikeAmplitudes.</p>}
    </>}
  </section>;
}

export function SossaMetrics({ dataset, selectedClusters = [], onOpenCurator, onLoadExample }) {
  const labelChannel = useChannelLabel();
  const channelState = useChannelClusters(dataset, selectedClusters);
  const { clusters } = channelState;
  const all = dataset?.clusters || [];
  const separations = useMemo(() => amplitudeSeparations(all), [all]);
  const channels = [...new Set(clusters.map((cluster) => cluster.primaryChannel).filter((channel) => channel !== null && channel !== undefined))];
  return <section className="sossa-widget" data-sossa="metrics">
    {gate(dataset, onOpenCurator, onLoadExample) || <>
      {sourceNotice(dataset, selectedClusters, onLoadExample)}
      <div className="sossa-controls"><ChannelPicker state={channelState} label="SOSSA metrics channel" /></div>
      <p className="sossa-note" role="note">{SEPARATION_HELP}</p>
      <p>Calculated across all uploaded clusters on the same primary channel, independently of selection and smoothing.</p>
      {clusters.length ? <FlexibleTable exportName="SossaWidgets"><thead><tr><th>Cluster</th><th>Channel</th><th>Coverage</th><th>Separation</th></tr></thead>
        <tbody>{clusters.map((cluster) => <tr key={cluster.id}><td><span className="sossa-cluster-dot" style={{ backgroundColor: color(cluster.id) }} />{cluster.id}</td><td>{cluster.primaryChannel ?? 'Unavailable'}</td>
          <td>{format(sossaCoverage(cluster))}</td><td>{format(separations.get(String(cluster.id)))}</td></tr>)}</tbody></FlexibleTable> : <p>Select clusters in the curator.</p>}
      {channels.map((channel) => {
        const peers = all.filter((cluster) => String(cluster.primaryChannel) === String(channel));
        const coverages = peers.map(sossaCoverage);
        const sameDenominator = peers.every((cluster) => cluster.num_fs_spikes === peers[0].num_fs_spikes);
        const sum = coverages.reduce((total, value) => total + (value ?? 0), 0);
        const value = sameDenominator && coverages.every((coverage) => coverage !== null) && sum <= 1 ? sum : null;
        return <p key={channel}>Channel {labelChannel(channel)} coverage: {format(value)}</p>;
      })}
    </>}
  </section>;
}
